import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Layers,
  Thermometer,
  AlertTriangle,
  RotateCcw,
  ArrowRight,
  TrendingUp,
  Activity,
  Flame,
  ShieldCheck,
  FileText,
  ClipboardList,
  Users,
  Truck,
  Download,
} from "lucide-react";

type OverviewData = {
  stats: {
    total_windrows: number;
    active_windrows: number;
    complete_windrows: number;
    ready_windrows: number;
    total_cubic_yards: number;
    alert_count: number;
    avg_temp: number;
  };
  temp_alerts: Array<{
    row_id: string;
    name: string;
    temperature: number;
    recorded_at: string;
  }>;
  recent_activity: Array<{
    type: string;
    who: string;
    windrow: string;
    detail: string;
    ts: string;
  }>;
  temp_trend?: Array<{ day: string; avg_temp: number; reading_count: number }>;
  turns_by_day?: Array<{ day: string; turns: number }>;
  ops_summary?: {
    bols_today: number;
    bols_week: number;
    bols_month_tons: number;
    active_work_orders: number;
    total_clients: number;
    total_bols: number;
  };
};

export default function DashboardOverview() {
  const { token } = useAuth();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/operations/overview", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d != null && setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const stats = data?.stats;
  const complianceRate = stats
    ? stats.active_windrows > 0
      ? Math.round(((stats.active_windrows - stats.alert_count) / stats.active_windrows) * 100)
      : 100
    : 0;

  const tempTrend = data?.temp_trend ?? [];
  const turnsByDay = data?.turns_by_day ?? [];
  const ops = data?.ops_summary;
  const windrowBars = (data?.temp_alerts ?? []).slice().sort((a, b) => b.temperature - a.temperature);
  const maxBarTemp = Math.max(170, ...windrowBars.map((w) => w.temperature));
  const trendMax = Math.max(170, ...tempTrend.map((t) => t.avg_temp || 0));
  const trendMin = Math.min(110, ...tempTrend.map((t) => t.avg_temp || 999));
  const trendRange = Math.max(1, trendMax - trendMin);

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="heading-display text-3xl text-foreground">Operations Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time facility overview and quick actions
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold shadow-sm transition-colors"
        >
          <Download size={16} />
          Generate EPA Compliance Report
        </button>
      </div>

      {/* Stats grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
              <div className="h-3 bg-muted rounded w-20 mb-4" />
              <div className="h-9 bg-muted rounded w-16" />
            </div>
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-xl p-5 hover:border-forest-300 dark:hover:border-forest-800 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <span className="label-caps text-muted-foreground">Active Windrows</span>
              <div className="w-9 h-9 rounded-lg bg-forest-100 dark:bg-forest-900/30 flex items-center justify-center">
                <Layers size={17} className="text-forest-600 dark:text-forest-400" />
              </div>
            </div>
            <p className="stat-value text-3xl text-foreground">{stats.active_windrows}</p>
            <p className="text-xs text-muted-foreground mt-1">{stats.total_windrows} total</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-5 hover:border-amber-300 dark:hover:border-amber-800 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <span className="label-caps text-muted-foreground">Avg Temperature</span>
              <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Flame size={17} className="text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="stat-value text-3xl text-foreground">{stats.avg_temp}<span className="text-lg text-muted-foreground ml-0.5">°F</span></p>
            <p className="text-xs text-muted-foreground mt-1">across active rows</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-5 hover:border-blue-300 dark:hover:border-blue-800 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <span className="label-caps text-muted-foreground">Compliance</span>
              <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <ShieldCheck size={17} className="text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="stat-value text-3xl text-foreground">{complianceRate}<span className="text-lg text-muted-foreground ml-0.5">%</span></p>
            <p className="text-xs text-muted-foreground mt-1">above threshold</p>
          </div>

          <div className={`bg-card border rounded-xl p-5 transition-colors ${
            stats.alert_count > 0
              ? "border-red-200 dark:border-red-900 hover:border-red-300 dark:hover:border-red-800"
              : "border-border hover:border-forest-300 dark:hover:border-forest-800"
          }`}>
            <div className="flex items-center justify-between mb-3">
              <span className="label-caps text-muted-foreground">Temp Alerts</span>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                stats.alert_count > 0
                  ? "bg-red-100 dark:bg-red-900/30"
                  : "bg-forest-100 dark:bg-forest-900/30"
              }`}>
                <AlertTriangle size={17} className={stats.alert_count > 0 ? "text-red-600 dark:text-red-400" : "text-forest-600 dark:text-forest-400"} />
              </div>
            </div>
            <p className={`stat-value text-3xl ${stats.alert_count > 0 ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>
              {stats.alert_count}
            </p>
            <p className="text-xs text-muted-foreground mt-1">below 131°F</p>
          </div>
        </div>
      )}

      {/* Operations summary tiles */}
      {ops && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/app/ops/bols" className="bg-card border border-border rounded-xl p-4 hover:border-forest-300 dark:hover:border-forest-800 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="label-caps text-muted-foreground">BOLs Today</span>
              <FileText size={16} className="text-forest-600 dark:text-forest-400" />
            </div>
            <p className="stat-value text-2xl text-foreground">{ops.bols_today}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{ops.bols_week} this week</p>
          </Link>
          <Link to="/app/ops/work-orders" className="bg-card border border-border rounded-xl p-4 hover:border-blue-300 dark:hover:border-blue-800 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="label-caps text-muted-foreground">Active Work Orders</span>
              <ClipboardList size={16} className="text-blue-600 dark:text-blue-400" />
            </div>
            <p className="stat-value text-2xl text-foreground">{ops.active_work_orders}</p>
            <p className="text-xs text-muted-foreground mt-0.5">pending or in progress</p>
          </Link>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="label-caps text-muted-foreground">Tonnage (30 day)</span>
              <Truck size={16} className="text-amber-600 dark:text-amber-400" />
            </div>
            <p className="stat-value text-2xl text-foreground">{ops.bols_month_tons}<span className="text-base text-muted-foreground ml-1">t</span></p>
            <p className="text-xs text-muted-foreground mt-0.5">across {ops.total_bols} loads</p>
          </div>
          <Link to="/app/ops/clients" className="bg-card border border-border rounded-xl p-4 hover:border-purple-300 dark:hover:border-purple-800 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="label-caps text-muted-foreground">Active Clients</span>
              <Users size={16} className="text-purple-600 dark:text-purple-400" />
            </div>
            <p className="stat-value text-2xl text-foreground">{ops.total_clients}</p>
            <p className="text-xs text-muted-foreground mt-0.5">in directory</p>
          </Link>
        </div>
      )}

      {/* Charts row: bar chart + trend line */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar chart: Temperature per active windrow */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading-display text-lg text-foreground">Temperature by Windrow</h2>
            <span className="text-xs text-muted-foreground">PFRP target: 131°F</span>
          </div>
          {windrowBars.length > 0 ? (
            <div className="space-y-2.5">
              {windrowBars.map((w) => {
                const pct = Math.min(100, (w.temperature / maxBarTemp) * 100);
                const isBad = w.temperature < 131;
                return (
                  <div key={w.row_id} className="grid grid-cols-[80px_1fr_60px] gap-3 items-center">
                    <div className="text-xs">
                      <p className="font-semibold text-foreground truncate">{w.row_id}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{w.name}</p>
                    </div>
                    <div className="relative h-7 bg-muted/40 rounded-md overflow-hidden">
                      {/* Threshold marker at 131°F */}
                      <div
                        className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-400 dark:border-red-500 z-10"
                        style={{ left: `${(131 / maxBarTemp) * 100}%` }}
                      />
                      <div
                        className={`h-full rounded-md transition-all ${
                          isBad
                            ? "bg-gradient-to-r from-red-400 to-red-500"
                            : w.temperature >= 150
                            ? "bg-gradient-to-r from-amber-400 to-amber-600"
                            : "bg-gradient-to-r from-forest-400 to-forest-600"
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className={`stat-value text-sm text-right tabular-nums ${isBad ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>
                      {w.temperature}°F
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">No active windrows</p>
          )}
        </div>

        {/* 7-day trend line chart */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading-display text-lg text-foreground">7-Day Temperature Trend</h2>
            <span className="text-xs flex items-center gap-1 text-forest-600 dark:text-forest-400">
              <TrendingUp size={12} /> facility avg
            </span>
          </div>
          {tempTrend.length > 0 ? (
            <div>
              <svg viewBox="0 0 400 160" className="w-full h-40">
                {/* Threshold line at 131°F */}
                {(() => {
                  const y131 = 150 - ((131 - trendMin) / trendRange) * 130;
                  return (
                    <>
                      <line x1="0" y1={y131} x2="400" y2={y131} stroke="#ef4444" strokeWidth="1" strokeDasharray="4 4" opacity="0.6" />
                      <text x="395" y={y131 - 4} textAnchor="end" fontSize="9" fill="#ef4444" opacity="0.8">131°F</text>
                    </>
                  );
                })()}
                {/* Trend area */}
                <path
                  d={
                    "M " +
                    tempTrend
                      .map((p, i) => {
                        const x = (i / Math.max(1, tempTrend.length - 1)) * 380 + 10;
                        const y = 150 - ((p.avg_temp - trendMin) / trendRange) * 130;
                        return `${x},${y}`;
                      })
                      .join(" L ") +
                    ` L ${(tempTrend.length - 1) / Math.max(1, tempTrend.length - 1) * 380 + 10},155 L 10,155 Z`
                  }
                  fill="url(#trendGrad)"
                  opacity="0.3"
                />
                <defs>
                  <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3a8a40" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#3a8a40" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {/* Trend line */}
                <polyline
                  points={tempTrend
                    .map((p, i) => {
                      const x = (i / Math.max(1, tempTrend.length - 1)) * 380 + 10;
                      const y = 150 - ((p.avg_temp - trendMin) / trendRange) * 130;
                      return `${x},${y}`;
                    })
                    .join(" ")}
                  fill="none"
                  stroke="#3a8a40"
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                />
                {/* Data points */}
                {tempTrend.map((p, i) => {
                  const x = (i / Math.max(1, tempTrend.length - 1)) * 380 + 10;
                  const y = 150 - ((p.avg_temp - trendMin) / trendRange) * 130;
                  return <circle key={p.day} cx={x} cy={y} r="3.5" fill="#3a8a40" stroke="white" strokeWidth="1.5" />;
                })}
              </svg>
              <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
                {tempTrend.map((p) => (
                  <span key={p.day} className="tabular-nums">
                    {new Date(p.day).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">No trend data yet</p>
          )}
        </div>
      </div>

      {/* Turns bar chart */}
      {turnsByDay.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading-display text-lg text-foreground">Turns Logged (7 days)</h2>
            <span className="text-xs text-muted-foreground">{turnsByDay.reduce((s, d) => s + d.turns, 0)} total</span>
          </div>
          <div className="flex items-end gap-2 h-28">
            {turnsByDay.map((d) => {
              const max = Math.max(...turnsByDay.map((x) => x.turns), 1);
              const h = (d.turns / max) * 100;
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex items-end h-24">
                    <div
                      className="w-full bg-gradient-to-t from-blue-500 to-blue-400 dark:from-blue-600 dark:to-blue-500 rounded-t"
                      style={{ height: `${h}%`, minHeight: d.turns > 0 ? "4px" : "0" }}
                      title={`${d.turns} turns`}
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground tabular-nums">
                    {new Date(d.day).toLocaleDateString("en-US", { weekday: "short" })}
                  </p>
                  <p className="text-xs font-semibold text-foreground">{d.turns}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
        {/* Quick actions — 3 columns (self-start: never stretch to match sibling column height) */}
        <div className="lg:col-span-3 self-start w-full bg-card border border-border rounded-xl p-4 sm:p-5">
          <h2 className="heading-display text-lg text-foreground mb-3">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Link
              to="/app/log-temp"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border bg-background hover:bg-amber-50 dark:hover:bg-amber-900/10 hover:border-amber-200 dark:hover:border-amber-800 transition-all group"
            >
              <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center group-hover:scale-110 transition-transform shrink-0">
                <Thermometer size={22} className="text-amber-600 dark:text-amber-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">Log Temperature</p>
                <p className="text-xs text-muted-foreground mt-0.5">Record reading</p>
              </div>
            </Link>
            <Link
              to="/app/log-turn"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border bg-background hover:bg-blue-50 dark:hover:bg-blue-900/10 hover:border-blue-200 dark:hover:border-blue-800 transition-all group"
            >
              <div className="w-11 h-11 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform shrink-0">
                <RotateCcw size={22} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">Record Turn</p>
                <p className="text-xs text-muted-foreground mt-0.5">Log turning event</p>
              </div>
            </Link>
            <Link
              to="/app/windrows"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border bg-background hover:bg-forest-50 dark:hover:bg-forest-900/10 hover:border-forest-200 dark:hover:border-forest-800 transition-all group"
            >
              <div className="w-11 h-11 rounded-xl bg-forest-100 dark:bg-forest-900/30 flex items-center justify-center group-hover:scale-110 transition-transform shrink-0">
                <Layers size={22} className="text-forest-600 dark:text-forest-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">Windrows</p>
                <p className="text-xs text-muted-foreground mt-0.5">{stats?.active_windrows || 0} active</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Temperature alerts — 2 columns */}
        <div className="lg:col-span-2 self-start w-full bg-card border border-border rounded-xl p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading-display text-lg text-foreground">Temp Alerts</h2>
            {data && data.temp_alerts.filter((t) => t.temperature < 131).length > 0 && (
              <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2.5 py-1 rounded-full font-semibold">
                {data.temp_alerts.filter((t) => t.temperature < 131).length} below
              </span>
            )}
          </div>
          {data && data.temp_alerts.length > 0 ? (
            <div className="space-y-2.5">
              {data.temp_alerts.map((alert) => {
                const isBad = alert.temperature < 131;
                return (
                  <div
                    key={alert.row_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isBad
                        ? "bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50"
                        : "bg-background border border-border"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-foreground">{alert.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{alert.row_id}</p>
                    </div>
                    <div className="text-right">
                      <p className={`stat-value text-lg ${isBad ? "text-red-600 dark:text-red-400" : "text-forest-600 dark:text-forest-400"}`}>
                        {alert.temperature}°F
                      </p>
                      {isBad && <p className="text-[10px] text-red-500 font-semibold uppercase tracking-wide">Below min</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <ShieldCheck size={28} className="text-forest-400/50 mb-2" />
              <p className="text-sm text-muted-foreground">All clear — no alerts</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="heading-display text-lg text-foreground">Recent Activity</h2>
          <Link to="/app/reports" className="text-xs font-medium text-forest-600 dark:text-forest-400 hover:underline flex items-center gap-1">
            View reports <ArrowRight size={12} />
          </Link>
        </div>
        {data && data.recent_activity.length > 0 ? (
          <div className="space-y-1">
            {data.recent_activity.map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-3 border-b border-border/50 last:border-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  item.type === "temperature"
                    ? "bg-amber-100 dark:bg-amber-900/20"
                    : "bg-blue-100 dark:bg-blue-900/20"
                }`}>
                  {item.type === "temperature" ? (
                    <Thermometer size={14} className="text-amber-600 dark:text-amber-400" />
                  ) : (
                    <RotateCcw size={14} className="text-blue-600 dark:text-blue-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">
                    <span className="font-semibold">{item.who || "Operator"}</span>{" "}
                    <span className="text-muted-foreground">
                      {item.type === "temperature" ? "logged" : "recorded"}
                    </span>{" "}
                    <span className="font-semibold">{item.detail}{item.type === "temperature" ? "°F" : ""}</span>
                    <span className="text-muted-foreground"> on </span>
                    <span className="font-medium">{item.windrow}</span>
                  </p>
                </div>
                <p className="text-xs text-muted-foreground shrink-0 tabular-nums">
                  {new Date(item.ts).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10">
            <Activity size={28} className="mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground">No recent activity yet</p>
          </div>
        )}
      </div>

      {/* Bottom stats row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Windrows", value: stats.total_windrows, color: "text-foreground" },
            { label: "Cooking Complete", value: stats.complete_windrows, color: "text-blue-600 dark:text-blue-400" },
            { label: "Ready for Pickup", value: stats.ready_windrows, color: "text-purple-600 dark:text-purple-400" },
            { label: "Total Volume", value: `${Math.round(stats.total_cubic_yards)} yd³`, color: "text-foreground" },
          ].map((item) => (
            <div key={item.label} className="bg-card border border-border rounded-xl p-4 text-center">
              <p className="label-caps text-muted-foreground mb-2">{item.label}</p>
              <p className={`stat-value text-2xl ${item.color}`}>{item.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
